export const REDUCE_MOTION: boolean =
  typeof window !== "undefined" && !!window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Téma (svetlá/tmavá) — class na <html>, uložené do localStorage, meta theme-color sync.
export function isDarkTheme(): boolean {
  try { return localStorage.getItem("buzer_theme") === "dark"; } catch { return false; }
}
export function setTheme(dark: boolean): void {
  document.documentElement.classList.toggle("dark", !!dark);
  try { localStorage.setItem("buzer_theme", dark ? "dark" : "light"); } catch { /* noop */ }
  const m = document.querySelector("meta[name=theme-color]");
  if (m) (m as HTMLMetaElement).content = dark ? "#211c18" : "#EDE3D6";
}

// v8/v16/v17 redizajn — app colour themes (accent token-sets over the cream base).
// v17 — full palette; "black" is now a normal PRIMARY accent (ink on cream), NOT dark mode.
// `indigo` kept for back-compat with saved themes (not shown in the picker grid).
export const APP_THEMES = [
  "green", "emerald", "teal", "turquoise", "slate", "blue",
  "purple", "violet", "gold", "amber", "terracotta", "red",
  "rose", "pink", "black", "indigo",
  // v20 §7 — extra vivid themes
  "cobalt", "aqua", "jade", "lime", "tangerine", "cyber", "fuchsia", "ultraviolet",
] as const;
export type AppTheme = typeof APP_THEMES[number];
const THEME_META = "#EDE7DA";  // all themes share the warm cream base
function _coerce(t: string): AppTheme {
  return (APP_THEMES as readonly string[]).includes(t) ? (t as AppTheme) : "green";
}
export function getAppTheme(): AppTheme {
  try {
    const t = localStorage.getItem("buzer_app_theme");
    if (t) return _coerce(t);
  } catch { /* noop */ }
  return "green";
}
export function applyAppTheme(name: string, persist = true): void {
  const t: AppTheme = _coerce(name);
  const html = document.documentElement;
  html.setAttribute("data-theme", t);
  html.classList.remove("dark");  // v17 — no dark mode; every theme (incl. black) is a light-base accent
  if (persist) {
    try {
      localStorage.setItem("buzer_app_theme", t);
      localStorage.setItem("buzer_theme", "light");
    } catch { /* noop */ }
  }
  const m = document.querySelector("meta[name=theme-color]");
  if (m) (m as HTMLMetaElement).content = THEME_META;
}

// v24 — shop preview: ukáž tému na pár sekúnd, potom vráť pôvodnú (bez zápisu do localStorage)
let _previewTimer: ReturnType<typeof setTimeout> | null = null;
export function previewAppTheme(name: string, revertTo: string, ms = 3000): void {
  if (_previewTimer) clearTimeout(_previewTimer);
  applyAppTheme(name, false);
  _previewTimer = setTimeout(() => { applyAppTheme(revertTo, false); _previewTimer = null; }, ms);
}

// v24/v34 — odomykateľné písma z obchodu menia NADPISY + mono-labely (display+mono),
// nie telo (--font-ui). '' = predvolené → Bricolage Grotesque cez --ff-display/--ff-mono.
const FONT_FAMILY: Record<string, string> = {
  nunito: "'Nunito', system-ui, sans-serif",
  poppins: "'Poppins', system-ui, sans-serif",
  schibsted: "'Schibsted Grotesk', system-ui, sans-serif",
  fraunces: "'Fraunces', 'Newsreader', Georgia, serif",  // pôvodný serif nadpisov
  bricolage: "'Bricolage Grotesque', system-ui, sans-serif",
};
export function applyFont(code: string, persist = true): void {
  const fam = FONT_FAMILY[code] || "";
  const html = document.documentElement;
  // prepisujeme RAW tokeny (--ff-display/--ff-mono), nie aliasy — časť komponentov
  // číta --ff-display/--ff-mono priamo, takže inak by sa font zmenil len sčasti.
  if (fam) {
    html.style.setProperty("--ff-display", fam);
    html.style.setProperty("--ff-mono", fam);
  } else {
    html.style.removeProperty("--ff-display");
    html.style.removeProperty("--ff-mono");
  }
  if (persist) { try { localStorage.setItem("buzer_app_font", code || ""); } catch { /* noop */ } }
}
export function getAppFont(): string {
  try { return localStorage.getItem("buzer_app_font") || ""; } catch { return ""; }
}
let _fontTimer: ReturnType<typeof setTimeout> | null = null;
export function previewFont(code: string, revertTo: string, ms = 3000): void {
  if (_fontTimer) clearTimeout(_fontTimer);
  applyFont(code, false);
  _fontTimer = setTimeout(() => { applyFont(revertTo, false); _fontTimer = null; }, ms);
}
